import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, ErrorMsg, Input, StatusChip, Text } from '@fragments';
import { IconBolt, IconBrandInstagram, IconChevronLeft, IconClockHour4, IconExternalLink, IconInbox, IconLogin, IconPaperclip, IconRefresh, IconSend, IconX } from '@tabler/icons-react';
import { STUDIO_AUTOMATIONS, automationPath } from '../../enums/routes';
import SocialAvatar from '../../components/SocialAvatar';
import ConnectHero from '../Automations/components/ConnectHero';
import PageState from '../Automations/components/PageState';
import { formatRelative, formatTime } from '../Automations/utils/format';
import useInbox from './hooks/useInbox';
import './styles.scss';

const IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_TEXT = 1000;
const nameOf = participant => (participant.handle ? `@${participant.handle}` : participant.name || '');

function ConversationRow({ studio: s, conversation, selected, onOpen }) {
  const { t } = useTranslation('studioAutomations');
  const { participant, last_message: last, unread_count: unread, window: openWindow } = conversation;
  return <li>
    <button type="button" className={`studio-inbox__row${selected ? ' is-open' : ''}`} aria-current={selected ? 'true' : undefined} onClick={onOpen}>
      <SocialAvatar className="studio-inbox__avatar" picture={participant.picture} label={participant.handle || participant.name} />
      <span className="studio-inbox__row-text">
        <span className="studio-inbox__row-line">
          <Text.label contrast={unread ? 10 : 8} maxLines={1}>{nameOf(participant)}</Text.label>
          {last && <Text.caption contrast={6}>{formatRelative(last.timestamps.sent, s.locale)}</Text.caption>}
        </span>
        <span className="studio-inbox__row-line">
          <Text.span contrast={unread ? 8 : 6} maxLines={1}>{last ? `${last.direction === 'outbound' ? t('inboxYou') : ''}${last.text || t('inboxAttachment')}` : t('inboxNoMessages')}</Text.span>
          {unread > 0 ? <span className="studio-inbox__count" aria-label={t('inboxUnreadCount', { count: unread })}>{unread}</span> : !openWindow.open && <IconClockHour4 className="studio-inbox__closed" aria-label={t('inboxWindowClosedShort')} />}
        </span>
      </span>
    </button>
  </li>;
}

function Bubble({ studio: s, message }) {
  const { t } = useTranslation('studioAutomations');
  const inbound = message.direction === 'inbound';
  return <div className={`studio-inbox__bubble ${inbound ? 'is-in' : 'is-out'}${message.status === 'failed' ? ' is-failed' : ''}`}>
    {message.reply_to_story && <div className="studio-inbox__story">
      {message.reply_to_story.url && <img src={message.reply_to_story.url} alt="" />}
      <Text.caption contrast={6} left={<IconBrandInstagram />}>{t('inboxStoryReply')}</Text.caption>
    </div>}
    {message.attachments.map((attachment, index) => (attachment.type === 'image' && attachment.url
      ? <img key={index} className="studio-inbox__image" src={attachment.url} alt={t('inboxAttachment')} />
      : <Text.caption key={index} contrast={6} left={<IconPaperclip />}>{t(`attachment_${attachment.type}`)}</Text.caption>))}
    {message.text && <p>{message.text}</p>}
    <span className="studio-inbox__meta">
      {message.origin === 'automation' && (message.automation_id
        ? <button type="button" className="studio-inbox__origin" onClick={() => s.navigate(automationPath(message.automation_id))}><Text.caption contrast={6} left={<IconBolt />}>{t('inboxOriginAutomation')}</Text.caption></button>
        : <Text.caption contrast={6} left={<IconBolt />}>{t('inboxOriginAutomation')}</Text.caption>)}
      {message.origin === 'instagram_app' && <Text.caption contrast={6} left={<IconExternalLink />}>{t('inboxOriginApp')}</Text.caption>}
      <Text.caption contrast={6}>{formatTime(message.timestamps.sent, s.locale)}</Text.caption>
    </span>
    {message.status === 'failed' && message.error && <ErrorMsg>{message.error.message}</ErrorMsg>}
  </div>;
}

function Composer({ inbox, conversation }) {
  const { t } = useTranslation('studioAutomations');
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const file = useRef(null);
  const preview = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!image) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(image);
    preview.current = url;
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  useEffect(() => { setText(''); setImage(null); }, [conversation.id]);
  if (!conversation.window.open) return <div className="studio-inbox__window-closed" role="status">
    <IconClockHour4 />
    <span><Text.label contrast={8}>{t('inboxWindowClosed')}</Text.label><Text.caption contrast={6}>{t('inboxWindowClosedText')}</Text.caption></span>
  </div>;
  const submit = async () => {
    const clean = text.trim();
    if ((!clean && !image) || inbox.sending) return;
    if (await inbox.send({ text: clean, image })) { setText(''); setImage(null); }
  };
  return <div className="studio-inbox__composer">
    {image && <div className="studio-inbox__attachment">
      {previewUrl && <img src={previewUrl} alt="" />}
      <Button size="micro" variant="rounded" color="overlay" icon={<IconX />} aria-label={t('inboxRemoveImage')} preventLoading onClick={() => setImage(null)} />
    </div>}
    <div className="studio-inbox__composer-row">
      <Button variant="rounded" discreet color="overlay" icon={<IconPaperclip />} aria-label={t('inboxAttach')} tooltip={t('inboxAttach')} preventLoading onClick={() => file.current?.click()} />
      <input ref={file} type="file" hidden accept={IMAGE_TYPES.join(',')} onChange={event => { const [picked] = event.target.files; if (picked && IMAGE_TYPES.includes(picked.type)) setImage(picked); event.target.value = ''; }} />
      <Input fullWidth multiline rows={1} fitContent value={text} placeholder={t('inboxPlaceholder')} aria-label={t('inboxPlaceholder')} onInput={value => setText(value.slice(0, MAX_TEXT))}
        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit(); } }} />
      <Button variant="rounded" color="inverse" icon={<IconSend />} aria-label={t('inboxSend')} tooltip={t('inboxSend')} loading={inbox.sending} disabled={!text.trim() && !image} preventLoading onClick={submit} />
    </div>
    {inbox.sendError && <ErrorMsg>{t(inbox.sendError === 'messaging_window_closed' ? 'inboxWindowClosedText' : 'inboxSendFailed')}</ErrorMsg>}
    <Text.caption contrast={6}>{t('inboxTakeover')}</Text.caption>
  </div>;
}

function Thread({ studio: s, inbox }) {
  const { t } = useTranslation('studioAutomations');
  const { thread } = inbox;
  const scroller = useRef(null);
  const lastMessage = thread.messages[thread.messages.length - 1]?.id;
  // New messages land in view; older ones, loaded above, leave the reading position alone.
  useLayoutEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [lastMessage]);
  if (thread.state === 'loading' || (!thread.conversation && thread.state !== 'error')) return <div className="studio-inbox__thread" aria-busy="true"><PageState compact><Text.label loading>{t('inboxTitle')}</Text.label></PageState></div>;
  if (thread.state === 'error') return <div className="studio-inbox__thread"><PageState compact text={t('loadError')} action={<Button variant="rounded" color="overlay" preventLoading onClick={inbox.close}>{t('backToInbox')}</Button>} /></div>;
  const { conversation } = thread;
  const { participant, window: openWindow } = conversation;
  return <div className="studio-inbox__thread">
    <header className="studio-inbox__thread-head">
      <span className="studio-inbox__back"><Button variant="rounded" discreet color="overlay" icon={<IconChevronLeft />} aria-label={t('backToInbox')} preventLoading onClick={inbox.close} /></span>
      <SocialAvatar className="studio-inbox__avatar" picture={participant.picture} label={participant.handle || participant.name} />
      <span className="studio-inbox__thread-who">
        <Text.label contrast={9} maxLines={1}>{nameOf(participant)}</Text.label>
        {participant.name && participant.handle && <Text.caption contrast={6} maxLines={1}>{participant.name}</Text.caption>}
      </span>
      {openWindow.open
        ? <StatusChip.Default preserveCase text={t('inboxWindowUntil', { time: formatTime(openWindow.timestamps.closes, s.locale) })} />
        : <StatusChip.Alert preserveCase text={t('inboxWindowClosedShort')} />}
    </header>
    <div ref={scroller} className="studio-inbox__messages">
      {thread.more && <div className="studio-inbox__older"><Button size="small" variant="rounded" color="overlay" loading={inbox.olderLoading} preventLoading onClick={inbox.loadOlder}>{t('inboxOlder')}</Button></div>}
      {thread.messages.map(message => <Bubble key={message.id} studio={s} message={message} />)}
    </div>
    <Composer inbox={inbox} conversation={conversation} />
  </div>;
}

// /studio/inbox — the Direct of the active profile's account: the list beside the open conversation
// (one or the other on a phone), with Meta's 24-hour window said out loud.
export default function Inbox({ studio: s }) {
  const { t } = useTranslation('studioAutomations');
  const inbox = useInbox(s);
  if (!s.auth.signed) return <div className="studio-inbox"><PageState icon={<IconLogin />} text={t('signIn')} action={<Button variant="rounded" color="inverse" onClick={s.goToLogin}>{t('studio:login')}</Button>} /></div>;
  if (s.accountStatus === 'loading') return <div className="studio-inbox"><PageState><Text.h4 loading>{t('inboxTitle')}</Text.h4></PageState></div>;
  if (!s.profile) return <div className="studio-inbox studio-inbox--hero"><ConnectHero studio={s} variant="profile" context={t('inboxConnectText')} /></div>;
  if (inbox.status === 'no_connection') {
    const missing = s.automations.connected;
    return <div className="studio-inbox studio-inbox--hero"><ConnectHero studio={s} variant={missing ? 'permissions' : 'connect'} context={missing ? null : t('inboxConnectText')} /></div>;
  }
  if (inbox.status === 'error') return <div className="studio-inbox"><PageState icon={<IconInbox />} text={t('loadError')} action={<Button variant="rounded" color="overlay" startIcon={<IconRefresh />} preventLoading onClick={inbox.reload}>{t('studio:retry')}</Button>} /></div>;
  return <div className={`studio-inbox studio-inbox--panes${inbox.openId ? ' has-open' : ''}`}>
    <aside className="studio-inbox__list-pane">
      <div className="studio-inbox__list-head">
        <Text.h4 weight={400} contrast={9}>{t('inboxTitle')}</Text.h4>
        <ButtonGroup size="small" value={inbox.onlyUnread ? 'unread' : 'all'} onChange={value => inbox.setOnlyUnread(value === 'unread')} options={[{ value: 'all', label: t('inboxAll') }, { value: 'unread', label: t('inboxUnread') }]} />
      </div>
      {!inbox.conversations ? <ul className="studio-inbox__list" aria-busy="true">{[0, 1, 2, 3, 4].map(key => <li key={key} className="studio-inbox__row is-loading"><Text.label loading>{t('inboxTitle')}</Text.label></li>)}</ul>
        : !inbox.conversations.length ? <PageState compact icon={<IconInbox />} text={t(inbox.onlyUnread ? 'inboxNoneUnread' : 'inboxNone')} action={!inbox.onlyUnread && <Button size="small" variant="rounded" color="overlay" preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS)}>{t('title')}</Button>} />
        : <ul className="studio-inbox__list">{inbox.conversations.map(conversation => <ConversationRow key={conversation.id} studio={s} conversation={conversation} selected={conversation.id === inbox.openId} onOpen={() => inbox.open(conversation.id)} />)}</ul>}
    </aside>
    {inbox.openId ? <Thread studio={s} inbox={inbox} />
      : <div className="studio-inbox__empty"><PageState compact icon={<IconInbox />} text={t('inboxPick')} /></div>}
  </div>;
}
